import { useIntl } from '@/intl'
import {
  type BusFilterType,
  BusFilterValues,
} from '@/scopes/account/group-responsible/domain/types'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import { tok, tokPx } from '@sunwebgroup/ui-components'
import { Checkbox, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'
import {
  getFilteredParticipantsBusBooked,
  getParticipantsBusBookedCounts,
} from '../selectors'
import { EditSheetPortal } from './EditSheetPortal'
import { FilterToggleGroup } from './FilterToggleGroup'
import { ParticipantExtraInfoSheetPortal } from './ParticipantExtraInfoSheetPortal'
import {
  ChevronCell,
  EmptyTableMessage,
  Subtitle,
  Table,
  TableCell,
  TableCellWithSubtitle,
  TableHead,
  TableRow,
} from './Table'
import { TableSearch } from './TableSearch'
import { Toolbar } from './Toolbar'

const hideColumnsBreakpoint = 'primitives_breakpoint_m_max'
const showMobileLayoutBreakpoint = 'primitives_breakpoint_m_min'

const SelectableTableCellBase = css`
  &:first-child {
    flex-grow: 0;
  }

  width: fit-content;
  margin-right: -${tokPx('primitives_space_16')};
  margin-left: -${tokPx('primitives_space_16')};
  padding: ${tokPx('primitives_space_24')};
  position: relative;

  ${Checkbox} {
    position: relative;
  }

  @media (max-width: ${tokPx('primitives_breakpoint_m_min')}) {
    padding: 22px;
    margin-right: -${tokPx('primitives_space_12')};
    margin-left: -${tokPx('primitives_space_12')};
  }

  label {
    display: flex;
    align-items: center;
    padding-left: ${tokPx('primitives_space_16')};
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    cursor: pointer;

    @media (max-width: ${tokPx('primitives_breakpoint_m_min')}) {
      padding-left: ${tokPx('primitives_space_12')};
    }
  }
`

const TableCellSelectable = styled(TableCell)`
  ${SelectableTableCellBase}
`

const TableHeadSelectable = styled(TableHead)`
  ${SelectableTableCellBase}
`

const TableCellGrow = styled(TableCellWithSubtitle)`
  flex-grow: 1;
`

const TableCellGrowLoading = styled(TableCellGrow)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${tokPx('primitives_space_8')};
`

const TableCellWithSubtitleLoading = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-wrap: anywhere;
`

const TableHeadGrow = styled(TableHead)`
  flex-grow: 1;
`

const ProcessingMobile = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
  display: none;

@media (max-width: ${tokPx('primitives_breakpoint_m_min')}) {
    display: block;
  }
`

export function BusTable() {
  const [filter, setFilter] = React.useState<BusFilterType>('all')
  const [searchTerm, setSearchTerm] = React.useState('')
  const counts = useGroupResponsibleStore(getParticipantsBusBookedCounts)
  const filteredParticipantsAndBusInfo = useGroupResponsibleStore(state =>
    getFilteredParticipantsBusBooked(state, filter, searchTerm),
  )
  const selectedParticipantIds = useGroupResponsibleStore(state => state.selectedParticipantIds)
  const toggleParticipantSelection = useGroupResponsibleStore(state => state.toggleParticipantSelection)
  const changeExtraInfoId = useGroupResponsibleStore(state => state.changeExtraInfoId)
  const intl = useIntl()
  type CountKeys = keyof ReturnType<typeof getParticipantsBusBookedCounts>
  const filters: { value: BusFilterType, label: string, countKey: CountKeys }[] = [
    { value: BusFilterValues.ALL, label: 'groups_responsible_all', countKey: 'all' },
    { value: BusFilterValues.BOOKED, label: 'groups_responsible_status_booked', countKey: 'booked' },
    { value: BusFilterValues.NOT_BOOKED, label: 'groups_responsible_status_not_booked', countKey: 'notBooked' },
  ]

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value)
  }

  function handleFilterChange(newFilter: BusFilterType) {
    setFilter(newFilter)
  }

  const filteredParticipantIds = filteredParticipantsAndBusInfo
    .map(p => p.id)
  const isAllFilteredSelected = filteredParticipantIds.length > 0
    && filteredParticipantIds.every(id => selectedParticipantIds.has(id))

  function handleSelectAll() {
    if (isAllFilteredSelected) {
      filteredParticipantIds.forEach((id) => {
        if (selectedParticipantIds.has(id)) {
          toggleParticipantSelection(id)
        }
      })
    }
    else {
      filteredParticipantIds.forEach((id) => {
        if (!selectedParticipantIds.has(id)) {
          toggleParticipantSelection(id)
        }
      })
    }
  }

  return (
    <>
      <TableSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} />
      <FilterToggleGroup filter={filter} filters={filters} counts={counts} onFilterChange={handleFilterChange} />
      {filteredParticipantsAndBusInfo.length === 0
        ? (
            <EmptyTableMessage />
          )
        : (
            <>
              <Table>
                <thead>
                  <TableRow>
                    <TableHeadSelectable>
                      <label htmlFor="checkbox-all">
                        <Checkbox
                          id="checkbox-all"
                          checked={isAllFilteredSelected}
                          onCheckedChange={handleSelectAll}
                        />
                      </label>
                    </TableHeadSelectable>
                    <TableHeadGrow>
                      <Typography $variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_name_label' })}
                      </Typography>
                    </TableHeadGrow>
                    <TableHead $size="lg" $hideOn={showMobileLayoutBreakpoint}>
                      <Typography $variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_bus_stop_label' })}
                      </Typography>
                    </TableHead>
                    <TableHead $hideOn={hideColumnsBreakpoint}>
                      <Typography $variant="body-s-regular-tight">
                        {intl.formatMessage({ id: 'groups_responsible_table_bus_number_label' })}
                      </Typography>
                    </TableHead>
                    <TableHead $hideOn={showMobileLayoutBreakpoint} />
                  </TableRow>
                </thead>
                <tbody>
                  {filteredParticipantsAndBusInfo.map((participant) => {
                    const isSelected = selectedParticipantIds.has(participant.id)
                    const isBusProcessing = participant.isBusAssignmentProcessing

                    return (
                      <TableRow
                        key={participant.id}
                        $isSelected={isSelected}
                        onClick={() => changeExtraInfoId(participant.id)}
                      >
                        <TableCellSelectable onClick={(e) => {
                          e.stopPropagation()
                        }}
                        >
                          <label htmlFor={participant.id}>
                            <Checkbox
                              id={participant.id}
                              checked={isSelected}
                              onCheckedChange={() => {
                                toggleParticipantSelection(participant.id)
                              }}
                            />
                          </label>
                        </TableCellSelectable>
                        {!isBusProcessing
                          ? (
                              <TableCellGrow>
                                <Typography $variant="body-m-bold-tight">{participant.name}</Typography>
                                {participant.email && (
                                  <Subtitle>{participant.email}</Subtitle>
                                )}
                              </TableCellGrow>
                            )
                          : (
                              <TableCellGrowLoading>
                                <TableCellWithSubtitleLoading>
                                  <Typography $variant="body-m-bold-tight">{participant.name}</Typography>
                                  {participant.email && (
                                    <Subtitle>{participant.email}</Subtitle>
                                  )}
                                </TableCellWithSubtitleLoading>
                                <ProcessingMobile $variant="body-m-regular-tight">
                                  {intl.formatMessage({ id: 'groups_responsible_processing' })}
                                </ProcessingMobile>
                              </TableCellGrowLoading>
                            )}
                        <TableCell $size="lg" $hideOn={showMobileLayoutBreakpoint} $isMuted={isBusProcessing}>
                          {isBusProcessing
                            ? intl.formatMessage({ id: 'groups_responsible_processing' })
                            : (participant.busInfo?.location
                              ?? (participant.assigned
                                ? intl.formatMessage({ id: 'groups_responsible_table_self_drive_label' })
                                : '-'))}
                        </TableCell>
                        <TableCell $hideOn={hideColumnsBreakpoint} $isMuted={isBusProcessing}>
                          {isBusProcessing
                            ? intl.formatMessage({ id: 'groups_responsible_processing' })
                            : (participant.busInfo?.number ? participant.busInfo?.number : '-')}
                        </TableCell>
                        <ChevronCell $hideOn={showMobileLayoutBreakpoint} />
                      </TableRow>
                    )
                  })}
                </tbody>
              </Table>
              <ParticipantExtraInfoSheetPortal />
              <Toolbar />
              <EditSheetPortal />
            </>
          )}
    </>
  )
}
